/**
 * Copy a short string (a payment link) to the clipboard, in the browsers our
 * buyers actually use.
 *
 * `navigator.clipboard.writeText` is not a given on the checkout path:
 *
 *   • in-app WebViews (Instagram, Telegram) gate it behind a permission the
 *     host app never grants, so it REJECTS;
 *   • older Android WebViews and any non-secure context do not expose
 *     `navigator.clipboard` at all, so `?.writeText(…)` yields `undefined` —
 *     a `.catch()` chain on it throws, which is how a "copy" button can take
 *     the whole sheet down with an unhandled TypeError.
 *
 * So: try the async API, fall back to a hidden textarea + `execCommand('copy')`
 * (deprecated, and still the only thing those browsers honour), and report the
 * truth. A caller that gets `false` must not print «Скопировано» — the raw URL
 * stays on screen as the last route out.
 *
 * Never throws and never rejects.
 */
export async function copyLink(text: string): Promise<boolean> {
  try {
    const p = navigator.clipboard?.writeText(text);
    if (p) {
      await p;
      return true;
    }
  } catch {
    // Refused (permission) — the textarea path below may still work.
  }
  try {
    if (typeof document === 'undefined') return false;
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    // Off-screen but focusable. `position: fixed` is what keeps iOS from
    // scrolling the sheet out from under the buyer when selection moves here.
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand?.('copy') ?? false;
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
